import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, UserPlus, History, LogOut, QrCode, ArrowLeft, Calendar, 
  Users, Tag, CreditCard, Scan, Zap, Shield, Radio, Fingerprint,
  ChevronRight, Star, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { api } from '../services/api';
import { toast } from 'sonner';
import { useCustomer } from '../context/CustomerContext';
import QRScannerModal from '../components/QRScannerModal';
import CreditManagement from '../components/CreditManagement';

// Sons futuristes
const playBeep = () => {
  if (typeof window === 'undefined' || !window.AudioContext) return;
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.value = 800;
    gain.gain.setValueAtTime(0.1, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.1);
  } catch (e) {}
};

const playSuccess = () => {
  if (typeof window === 'undefined' || !window.AudioContext) return;
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [523, 659, 784].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.1, ctx.currentTime + i * 0.1);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.1 + 0.15);
      osc.start(ctx.currentTime + i * 0.1);
      osc.stop(ctx.currentTime + i * 0.1 + 0.15);
    });
  } catch (e) {}
};

// Fond spatial animé
const SpaceBackground = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none">
    {/* Étoiles */}
    {[...Array(100)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute rounded-full bg-white"
        style={{
          width: Math.random() * 2 + 1,
          height: Math.random() * 2 + 1,
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
        }}
        animate={{
          opacity: [0.2, 0.8, 0.2],
          scale: [1, 1.3, 1],
        }}
        transition={{
          duration: 2 + Math.random() * 3,
          repeat: Infinity,
          delay: Math.random() * 2,
        }}
      />
    ))}
    
    {/* Nébuleuses */}
    <div 
      className="absolute w-[800px] h-[800px] rounded-full opacity-15 blur-[180px]"
      style={{
        background: 'radial-gradient(circle, #00d4ff 0%, transparent 70%)',
        left: '-20%',
        top: '-10%',
      }}
    />
    <div 
      className="absolute w-[600px] h-[600px] rounded-full opacity-10 blur-[150px]"
      style={{
        background: 'radial-gradient(circle, #8b5cf6 0%, transparent 70%)',
        right: '-10%',
        bottom: '10%',
      }}
    />
    <div 
      className="absolute w-[400px] h-[400px] rounded-full opacity-10 blur-[100px]"
      style={{
        background: 'radial-gradient(circle, #ec4899 0%, transparent 70%)',
        left: '30%',
        bottom: '-5%',
      }}
    />
    
    {/* Grille holographique */}
    <div 
      className="absolute inset-0 opacity-5"
      style={{
        backgroundImage: `
          linear-gradient(to right, #00d4ff 1px, transparent 1px),
          linear-gradient(to bottom, #00d4ff 1px, transparent 1px)
        `,
        backgroundSize: '80px 80px',
      }}
    />
  </div>
);

// Carte de module avec effet holographique
const HoloCard = ({ children, color = 'cyan', className = '' }) => {
  const colors = {
    cyan: { border: '#00d4ff', glow: 'rgba(0, 212, 255, 0.3)' },
    purple: { border: '#8b5cf6', glow: 'rgba(139, 92, 246, 0.3)' },
    pink: { border: '#ec4899', glow: 'rgba(236, 72, 153, 0.3)' },
    orange: { border: '#f97316', glow: 'rgba(249, 115, 22, 0.3)' },
    green: { border: '#22c55e', glow: 'rgba(34, 197, 94, 0.3)' },
  };
  const c = colors[color];
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative rounded-2xl p-6 ${className}`}
      style={{
        background: 'linear-gradient(180deg, rgba(10, 15, 30, 0.9) 0%, rgba(5, 10, 20, 0.95) 100%)',
        border: `2px solid ${c.border}40`,
        boxShadow: `0 0 30px ${c.glow}, inset 0 0 20px ${c.glow}`,
        backdropFilter: 'blur(20px)',
      }}
    >
      {/* Coins lumineux */}
      <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 rounded-tl-lg" style={{ borderColor: c.border }} />
      <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 rounded-tr-lg" style={{ borderColor: c.border }} />
      <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 rounded-bl-lg" style={{ borderColor: c.border }} />
      <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 rounded-br-lg" style={{ borderColor: c.border }} />
      {children}
    </motion.div>
  );
};

// Bouton d'action principal
const ActionButton = ({ icon: Icon, label, onClick, color = 'cyan', size = 'normal' }) => {
  const colors = {
    cyan: 'from-cyan-500 to-blue-600 shadow-cyan-500/30',
    purple: 'from-purple-500 to-indigo-600 shadow-purple-500/30',
    pink: 'from-pink-500 to-rose-600 shadow-pink-500/30',
    orange: 'from-orange-500 to-red-600 shadow-orange-500/30',
    green: 'from-green-500 to-emerald-600 shadow-green-500/30',
  };
  
  return (
    <motion.button
      whileHover={{ scale: 1.02, boxShadow: '0 0 30px rgba(0, 212, 255, 0.4)' }}
      whileTap={{ scale: 0.98 }}
      onClick={() => { playBeep(); onClick?.(); }}
      className={`
        ${size === 'large' ? 'py-5 px-6' : 'py-4 px-5'}
        rounded-xl font-bold text-white
        bg-gradient-to-r ${colors[color]}
        shadow-lg transition-all duration-200
        flex items-center justify-center gap-2
      `}
    >
      <Icon className={size === 'large' ? 'w-6 h-6' : 'w-5 h-5'} />
      <span>{label}</span>
    </motion.button>
  );
};

// Onglet de navigation
const TabButton = ({ active, onClick, icon: Icon, label, color }) => {
  const colors = {
    cyan: { active: 'from-cyan-500 to-blue-600', inactive: 'border-cyan-500/30' },
    orange: { active: 'from-orange-500 to-red-600', inactive: 'border-orange-500/30' },
    yellow: { active: 'from-yellow-500 to-orange-500', inactive: 'border-yellow-500/30' },
  };
  const c = colors[color];
  
  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => { playBeep(); onClick?.(); }}
      className={`
        flex-1 py-4 px-4 rounded-xl font-semibold transition-all
        flex items-center justify-center gap-2
        ${active 
          ? `bg-gradient-to-r ${c.active} text-white shadow-lg` 
          : `bg-[#0a0f1a]/80 text-gray-400 border ${c.inactive} hover:bg-[#0a0f1a]`
        }
      `}
    >
      <Icon className="w-5 h-5" />
      <span className="hidden sm:inline">{label}</span>
    </motion.button>
  );
};

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newCustomerPhone, setNewCustomerPhone] = useState('');
  const [newCustomerPin, setNewCustomerPin] = useState('');
  const [newCustomerReferrer, setNewCustomerReferrer] = useState('');
  const [newCustomerBirthDate, setNewCustomerBirthDate] = useState('');
  const [newCustomerGuardianFirstName, setNewCustomerGuardianFirstName] = useState('');
  const [newCustomerGuardianLastName, setNewCustomerGuardianLastName] = useState('');
  const [newCustomerGuardianPhone, setNewCustomerGuardianPhone] = useState('');
  const [isMinor, setIsMinor] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { setActiveCustomer, vendorName, vendorCode, setIsVendorAuthenticated } = useCustomer();

  // États pour les onglets
  const [activeTab, setActiveTab] = useState('search');
  const [promoSearch, setPromoSearch] = useState('');
  const [foundProspect, setFoundProspect] = useState(null);
  const [isSearchingPromo, setIsSearchingPromo] = useState(false);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);

  // Auto-search si QR code dans l'URL
  useEffect(() => {
    const qrFromUrl = searchParams.get('qr');
    const tabFromUrl = searchParams.get('tab');
    const newCustomer = searchParams.get('new');
    
    // Récupérer le téléphone pré-sélectionné depuis l'admin
    const preSelectedPhone = sessionStorage.getItem('selectedCustomerPhone');
    if (preSelectedPhone) {
      sessionStorage.removeItem('selectedCustomerPhone'); // Nettoyer
      setPhone(preSelectedPhone);
      // Lancer la recherche automatiquement
      setTimeout(() => {
        searchCustomerByPhone(preSelectedPhone);
      }, 500);
      return;
    }
    
    if (tabFromUrl === 'credits') {
      setActiveTab('credits');
    } else if (newCustomer === '1') {
      setShowNewCustomerModal(true);
    } else if (qrFromUrl) {
      setPhone(qrFromUrl);
      setTimeout(() => handleSearchByQR(qrFromUrl), 500);
    } else {
      inputRef.current?.focus();
    }
  }, [searchParams]);

  // Recherche de prospect
  const handleSearchPromo = async () => {
    if (!promoSearch.trim()) return;
    setIsSearchingPromo(true);
    setFoundProspect(null);
    try {
      let prospect = null;
      try {
        prospect = await api.searchProspect({ code: promoSearch.trim().toUpperCase() });
      } catch (e) {
        try {
          prospect = await api.searchProspect({ phone: promoSearch.trim() });
        } catch (e2) {
          prospect = await api.searchProspect({ email: promoSearch.trim().toLowerCase() });
        }
      }
      setFoundProspect(prospect);
      playSuccess();
      toast.success('Prospect trouvé !');
    } catch (error) {
      toast.error('Aucun prospect trouvé');
    } finally {
      setIsSearchingPromo(false);
    }
  };

  const handleValidatePromo = async () => {
    if (!foundProspect) return;
    setIsValidatingPromo(true);
    try {
      await api.useProspectPromo(foundProspect.id, vendorName);
      playSuccess();
      toast.success('🎉 Bon -10% validé !');
      setFoundProspect({ ...foundProspect, promoUsed: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setIsValidatingPromo(false);
    }
  };

  const handleConvertToCustomer = async () => {
    if (!foundProspect) return;
    try {
      const result = await api.convertProspectToCustomer(foundProspect.id);
      if (result.success) {
        playSuccess();
        toast.success('Client créé !');
        navigate(`/team-lpe/customer/${result.customerId}`);
      } else {
        toast.info(result.message);
      }
    } catch (error) {
      toast.error('Erreur lors de la conversion');
    }
  };

  const handleSearchByQR = async (qrCode) => {
    setIsLoading(true);
    try {
      const customer = await api.findCustomerByQR(qrCode);
      setActiveCustomer(customer);
      playSuccess();
      toast.success(`Client trouvé: ${customer.name}`);
      navigate(`/team-lpe/customer/${customer.id}`);
    } catch (error) {
      toast.error('QR Code non trouvé');
    } finally {
      setIsLoading(false);
    }
  };

  // Calcul si mineur
  useEffect(() => {
    if (newCustomerBirthDate) {
      const birth = new Date(newCustomerBirthDate);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      setIsMinor(age < 18);
    } else {
      setIsMinor(false);
    }
  }, [newCustomerBirthDate]);

  // Recherche directe avec un téléphone spécifique
  const searchCustomerByPhone = async (phoneNumber) => {
    if (!phoneNumber?.trim()) return;
    setIsLoading(true);
    try {
      const customer = await api.searchCustomer(phoneNumber);
      setActiveCustomer(customer);
      playSuccess();
      toast.success(`Bienvenue ${customer.name} !`);
      navigate(`/team-lpe/customer/${customer.id}`);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Client non trouvé');
      } else {
        toast.error('Erreur lors de la recherche');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!phone.trim()) return;
    await searchCustomerByPhone(phone);
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    if (!newCustomerName.trim() || !newCustomerPhone.trim()) {
      toast.error('Nom et téléphone requis');
      return;
    }
    if (!newCustomerPin || newCustomerPin.length !== 4 || !/^\d{4}$/.test(newCustomerPin)) {
      toast.error('Le PIN doit être 4 chiffres');
      return;
    }

    setIsLoading(true);
    try {
      const customer = await api.createCustomer(
        newCustomerName, 
        newCustomerPhone, 
        newCustomerPin,
        newCustomerReferrer.trim() || null,
        newCustomerBirthDate || null,
        newCustomerGuardianFirstName.trim() || null,
        newCustomerGuardianLastName.trim() || null,
        newCustomerGuardianPhone.trim() || null
      );
      setActiveCustomer(customer);
      playSuccess();
      
      let successMsg = `Client ${customer.name} créé !`;
      if (newCustomerReferrer.trim()) successMsg += ' (Parrainage OK)';
      if (isMinor) successMsg += ' ⚠️ Mineur';
      toast.success(successMsg);
      
      // Reset
      setNewCustomerName('');
      setNewCustomerPhone('');
      setNewCustomerPin('');
      setNewCustomerReferrer('');
      setNewCustomerBirthDate('');
      setNewCustomerGuardianFirstName('');
      setNewCustomerGuardianLastName('');
      setNewCustomerGuardianPhone('');
      setIsMinor(false);
      setShowNewCustomerModal(false);
      navigate(`/team-lpe/customer/${customer.id}`);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response?.data?.detail || 'Ce numéro existe déjà');
      } else {
        toast.error('Erreur création client');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    setIsVendorAuthenticated(false);
    navigate('/team-lpe');
  };

  const handleQRScan = async (qrCode) => {
    setIsLoading(true);
    try {
      const customer = await api.getCustomerByQR(qrCode);
      setActiveCustomer(customer);
      playSuccess();
      toast.success(`Bienvenue ${customer.name} !`);
      navigate(`/team-lpe/customer/${customer.id}`);
    } catch (error) {
      toast.error('QR Code invalide');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030308] relative overflow-x-hidden overflow-y-auto">
      <SpaceBackground />
      
      {/* Container principal */}
      <div className="relative z-10 min-h-screen flex flex-col p-4 md:p-6">
        
        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-center mb-6"
        >
          <Button
            variant="ghost"
            onClick={() => navigate('/team-lpe')}
            className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 border border-cyan-500/30"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Sas
          </Button>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/30">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-green-400 text-sm font-medium">{vendorName}</span>
            </div>
            
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10 border border-red-500/30"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </motion.header>

        {/* Titre */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 mb-2">
            🛸 STATION ÉPICERIE
          </h1>
          <p className="text-gray-500 text-sm font-mono">SYSTÈME DE FIDÉLITÉ v3.0</p>
        </motion.div>

        {/* Onglets */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex gap-2 mb-6 max-w-2xl mx-auto w-full"
        >
          <TabButton 
            active={activeTab === 'search'} 
            onClick={() => setActiveTab('search')}
            icon={Users}
            label="Clients"
            color="cyan"
          />
          <TabButton 
            active={activeTab === 'credits'} 
            onClick={() => setActiveTab('credits')}
            icon={CreditCard}
            label="Crédits"
            color="orange"
          />
          <TabButton 
            active={activeTab === 'promo'} 
            onClick={() => setActiveTab('promo')}
            icon={Tag}
            label="Bons -10%"
            color="yellow"
          />
        </motion.div>

        {/* Contenu principal */}
        <div className="flex-1 max-w-2xl mx-auto w-full">
          <AnimatePresence mode="wait">
            
            {/* Onglet Clients */}
            {activeTab === 'search' && (
              <motion.div
                key="search"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-4"
              >
                {/* Recherche */}
                <HoloCard color="cyan">
                  <div className="flex items-center gap-2 mb-4">
                    <Scan className="w-5 h-5 text-cyan-400" />
                    <span className="text-cyan-400 font-mono text-sm">RECHERCHE CLIENT</span>
                  </div>
                  
                  <form onSubmit={handleSearch}>
                    <div className="relative mb-4">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-cyan-400" />
                      <Input
                        ref={inputRef}
                        type="text"
                        placeholder="Nom ou téléphone..."
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="pl-12 py-6 text-lg bg-[#0a0f1a] border-cyan-500/30 text-white placeholder:text-gray-600 focus:border-cyan-400 rounded-xl"
                        data-testid="phone-search-input"
                        autoComplete="off"
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      disabled={isLoading || !phone.trim()}
                      className="w-full py-6 text-lg font-bold bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 rounded-xl"
                      data-testid="search-button"
                    >
                      {isLoading ? (
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                          className="w-6 h-6 border-2 border-white border-t-transparent rounded-full"
                        />
                      ) : (
                        <>
                          <Search className="w-5 h-5 mr-2" />
                          Rechercher
                        </>
                      )}
                    </Button>
                  </form>
                  
                  {/* Aide recherche */}
                  <p className="text-xs text-gray-500 mt-3 text-center">
                    💡 Tapez le nom, prénom ou numéro de téléphone du client
                  </p>
                </HoloCard>

                {/* Actions rapides */}
                <div className="grid grid-cols-3 gap-3">
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => { playBeep(); setShowQRScanner(true); }}
                    className="py-5 rounded-xl bg-gradient-to-br from-purple-500/20 to-indigo-500/20 border border-purple-500/30 hover:border-purple-400 transition-all"
                  >
                    <QrCode className="w-8 h-8 mx-auto mb-2 text-purple-400" />
                    <span className="text-purple-400 text-sm font-medium">Scanner</span>
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => { playBeep(); setShowNewCustomerModal(true); }}
                    className="py-5 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 hover:border-green-400 transition-all"
                    data-testid="new-customer-button"
                  >
                    <UserPlus className="w-8 h-8 mx-auto mb-2 text-green-400" />
                    <span className="text-green-400 text-sm font-medium">Nouveau</span>
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => { playBeep(); navigate('/team-lpe/history'); }}
                    className="py-5 rounded-xl bg-gradient-to-br from-orange-500/20 to-red-500/20 border border-orange-500/30 hover:border-orange-400 transition-all"
                  >
                    <History className="w-8 h-8 mx-auto mb-2 text-orange-400" />
                    <span className="text-orange-400 text-sm font-medium">Historique</span>
                  </motion.button>
                </div>
              </motion.div>
            )}

            {/* Onglet Crédits */}
            {activeTab === 'credits' && (
              <motion.div
                key="credits"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
              >
                <HoloCard color="orange">
                  <CreditManagement vendorName={vendorName} />
                </HoloCard>
              </motion.div>
            )}

            {/* Onglet Bons Promo */}
            {activeTab === 'promo' && (
              <motion.div
                key="promo"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
              >
                <HoloCard color="pink">
                  <div className="flex items-center gap-2 mb-4">
                    <Tag className="w-5 h-5 text-pink-400" />
                    <span className="text-pink-400 font-mono text-sm">VALIDATION BON -10%</span>
                  </div>
                  
                  <div className="relative mb-4">
                    <Fingerprint className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-pink-400" />
                    <Input
                      placeholder="Code promo, téléphone ou email..."
                      value={promoSearch}
                      onChange={(e) => setPromoSearch(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearchPromo()}
                      className="pl-12 py-6 bg-[#0a0f1a] border-pink-500/30 text-white placeholder:text-gray-600 focus:border-pink-400 rounded-xl"
                    />
                  </div>
                  
                  <Button
                    onClick={handleSearchPromo}
                    disabled={isSearchingPromo || !promoSearch.trim()}
                    className="w-full py-5 font-bold bg-gradient-to-r from-pink-500 to-rose-600 rounded-xl mb-4"
                  >
                    {isSearchingPromo ? 'Recherche...' : 'Rechercher le bon'}
                  </Button>

                  {/* Résultat prospect */}
                  <AnimatePresence>
                    {foundProspect && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="mt-4 p-4 rounded-xl bg-[#0a0f1a] border border-pink-500/30"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center">
                            <Star className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <p className="text-white font-bold">{foundProspect.prenom}</p>
                            <p className="text-gray-400 text-sm">{foundProspect.email}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 mb-3">
                          <span className={`px-3 py-1 rounded-full text-sm font-mono ${
                            foundProspect.promoUsed 
                              ? 'bg-gray-500/20 text-gray-400 line-through' 
                              : 'bg-green-500/20 text-green-400'
                          }`}>
                            🎫 {foundProspect.codePromo}
                          </span>
                          {foundProspect.promoUsed && (
                            <span className="text-red-400 text-sm">Déjà utilisé</span>
                          )}
                        </div>
                        
                        {!foundProspect.promoUsed && (
                          <div className="flex gap-2">
                            <Button
                              onClick={handleValidatePromo}
                              disabled={isValidatingPromo}
                              className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl"
                            >
                              <Sparkles className="w-4 h-4 mr-2" />
                              Valider -10%
                            </Button>
                            <Button
                              onClick={handleConvertToCustomer}
                              variant="outline"
                              className="flex-1 border-cyan-500/50 text-cyan-400 rounded-xl"
                            >
                              <UserPlus className="w-4 h-4 mr-2" />
                              Créer compte
                            </Button>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </HoloCard>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <motion.footer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-8 pb-4"
        >
          <p className="text-gray-600 text-xs font-mono">
            <Zap className="w-3 h-3 inline mr-1" />
            LA PETITE ÉPICERIE • STATION SPATIALE v3.0
          </p>
        </motion.footer>
      </div>

      {/* Modals */}
      <QRScannerModal
        isOpen={showQRScanner}
        onClose={() => setShowQRScanner(false)}
        onScan={handleQRScan}
      />

      {/* Modal Nouveau Client */}
      <Dialog open={showNewCustomerModal} onOpenChange={setShowNewCustomerModal}>
        <DialogContent className="bg-[#0a0f1a] border border-cyan-500/30 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Nouveau Client-Malin
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateCustomer} className="space-y-4">
            <div>
              <Label className="text-gray-400">Nom complet *</Label>
              <Input
                value={newCustomerName}
                onChange={(e) => setNewCustomerName(e.target.value)}
                placeholder="Jean Dupont"
                className="bg-[#050810] border-gray-700 text-white"
                required
              />
            </div>
            
            <div>
              <Label className="text-gray-400">Téléphone *</Label>
              <Input
                type="tel"
                value={newCustomerPhone}
                onChange={(e) => setNewCustomerPhone(e.target.value)}
                placeholder="0612345678"
                className="bg-[#050810] border-gray-700 text-white"
                required
              />
            </div>
            
            <div>
              <Label className="text-gray-400">PIN (4 chiffres) *</Label>
              <Input
                type="text"
                maxLength={4}
                value={newCustomerPin}
                onChange={(e) => setNewCustomerPin(e.target.value.replace(/\D/g, ''))}
                placeholder="1234"
                className="bg-[#050810] border-gray-700 text-white font-mono text-center text-xl tracking-widest"
                required
              />
            </div>
            
            <div>
              <Label className="text-gray-400">Date de naissance</Label>
              <Input
                type="date"
                value={newCustomerBirthDate}
                onChange={(e) => setNewCustomerBirthDate(e.target.value)}
                className="bg-[#050810] border-gray-700 text-white"
              />
              {isMinor && (
                <p className="text-yellow-400 text-xs mt-1">⚠️ Client mineur détecté</p>
              )}
            </div>
            
            <div>
              <Label className="text-gray-400">Code parrain (optionnel)</Label>
              <Input
                value={newCustomerReferrer}
                onChange={(e) => setNewCustomerReferrer(e.target.value)}
                placeholder="Téléphone du parrain"
                className="bg-[#050810] border-gray-700 text-white"
              />
            </div>

            {isMinor && (
              <div className="p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/30 space-y-3">
                <p className="text-yellow-400 text-sm font-semibold">👨‍👩‍👧 Tuteur légal requis</p>
                <Input
                  value={newCustomerGuardianFirstName}
                  onChange={(e) => setNewCustomerGuardianFirstName(e.target.value)}
                  placeholder="Prénom du tuteur"
                  className="bg-[#050810] border-gray-700 text-white"
                />
                <Input
                  value={newCustomerGuardianLastName}
                  onChange={(e) => setNewCustomerGuardianLastName(e.target.value)}
                  placeholder="Nom du tuteur"
                  className="bg-[#050810] border-gray-700 text-white"
                />
                <Input
                  value={newCustomerGuardianPhone}
                  onChange={(e) => setNewCustomerGuardianPhone(e.target.value)}
                  placeholder="Téléphone du tuteur"
                  className="bg-[#050810] border-gray-700 text-white"
                />
              </div>
            )}
            
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowNewCustomerModal(false)}
                className="flex-1 border-gray-600"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600"
              >
                {isLoading ? 'Création...' : 'Créer le client'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
